import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { InviteAPI } from '../services/api';
import { inviteSchema, InviteFormData } from '../schemas/validation';
import AdminDataService from '../services/AdminDataService';
import { LoadingScreen } from './LoadingScreen';
import { AlertService } from '../services/AlertService';
import './AuthForms.css';

interface CreateInviteFormProps {
  onInviteCreated?: (invite: any) => void;
  onCancel?: () => void;
}

export const CreateInviteForm: React.FC<CreateInviteFormProps> = ({ onInviteCreated, onCancel }) => {
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [availableRoles, setAvailableRoles] = useState<{ value: string; label: string }[]>([]);
  const [availableSupervisors, setAvailableSupervisors] = useState<{ id: string; name: string; role: string }[]>([]);
  const adminService = AdminDataService.getInstance();

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
  } = useForm<InviteFormData>({
    resolver: zodResolver(inviteSchema),
    defaultValues: {
      role: 'agent',
    },
  });

  const selectedRole = watch('role');

  // Helper function to check if role requires supervisor
  const roleRequiresSupervisor = (role: string): boolean => {
    const rolesRequiringSupervisor = ['agent', 'processor_agent', 'sales_team_lead', 'processor_team_lead', 'floor_manager', 'director'];
    return rolesRequiringSupervisor.includes(role);
  };

  useEffect(() => {
    const fetchRoles = async () => {
      try {
        const roles = await adminService.getUserRoles();
        setAvailableRoles(roles);
      } catch (err) {
        console.warn('Could not fetch available roles, using default');
        setAvailableRoles([{ value: 'agent', label: 'Agent' }]);
      }
    };
    fetchRoles();
  }, [adminService]);

  // Fetch supervisors when role changes
  useEffect(() => {
    const fetchSupervisors = async () => {
      try {
        const supervisors = await adminService.getAvailableSupervisors(selectedRole);
        setAvailableSupervisors(
          supervisors.map(user => ({
            id: user.id,
            name: user.full_name || user.username,
            role: user.role
          }))
        );
      } catch (err) {
        console.warn('Could not fetch available supervisors');
        setAvailableSupervisors([]);
      }
    };

    if (selectedRole) {
      fetchSupervisors();
    }
  }, [selectedRole, adminService]);

  const onSubmit = async (data: InviteFormData) => {
    try {
      setError('');
      setSuccess('');
      
      // Validate supervisor requirement
      if (roleRequiresSupervisor(data.role) && !data.supervisor_id) {
        if (availableSupervisors.length === 0) {
          setError(`Cannot create ${data.role} invite: No supervisors available. Please create a supervisor first.`);
          return;
        } else {
          setError(`Supervisor is required for ${data.role} role. Please select a supervisor.`);
          return;
        }
      }
      
      setLoading(true);
      
      const invite = await InviteAPI.createInvite(data);
      
      setSuccess(`Invite created successfully! Token: ${invite.token}`);
      AlertService.success(`Invitation sent successfully to ${data.email}`);
      reset();
      
      if (onInviteCreated) {
        onInviteCreated(invite);
      }
    } catch (err: any) {
      // Handle specific error cases
      const errorMessage = err.message || 'Failed to create invite';
      
      // Check if it's a duplicate invitation error
      if (errorMessage.includes('already exists') || errorMessage.includes('pending invitation')) {
        setError(`⚠️ ${errorMessage}`);
        AlertService.warning(errorMessage);
      } else {
        setError(errorMessage);
        AlertService.error(errorMessage);
      }
      
      // Log error for debugging
      console.error('Invite creation error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-form-container">
      <form onSubmit={handleSubmit(onSubmit)} className="auth-form">
        <h2>Create Invitation</h2>
        
        {error && (
          <div className="error-message" style={{
            padding: '1rem',
            borderRadius: '8px',
            marginBottom: '1rem',
            background: 'rgba(211, 47, 47, 0.1)',
            border: '1px solid rgba(211, 47, 47, 0.3)'
          }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem' }}>
              <span style={{ fontSize: '1.2rem' }}>⚠️</span>
              <div style={{ flex: 1 }}>
                <div>{error}</div>
                {error.includes('already exists') && (
                  <div style={{ marginTop: '0.5rem', fontSize: '0.875rem', opacity: 0.9 }}>
                    <strong>Next steps:</strong>
                    <ul style={{ margin: '0.25rem 0 0 1rem', padding: 0 }}>
                      <li>Go to the invitations list to view existing invitations</li>
                      <li>Revoke the existing invitation if it's no longer needed</li>
                      <li>Or use the existing invitation token if it's still valid</li>
                    </ul>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
        {success && <div className="success-message">{success}</div>}
        
        <div className="form-group">
          <label htmlFor="email">Email Address</label>
          <input
            id="email"
            type="email"
            {...register('email')}
            className={errors.email ? 'error' : ''}
            placeholder="user@erpamperedeals.com"
            disabled={loading}
          />
          {errors.email && <span className="field-error">{errors.email.message}</span>}
        </div>

        <div className="form-group">
          <label htmlFor="role">Role</label>
          <select
            id="role"
            {...register('role')}
            className={errors.role ? 'error' : ''}
            disabled={loading}
          >
            {availableRoles.map(role => (
              <option key={role.value} value={role.value}>
                {role.label}
              </option>
            ))}
          </select>
          {errors.role && <span className="field-error">{errors.role.message}</span>}
        </div>

        {roleRequiresSupervisor(selectedRole) && (
          <div className="form-group">
            <label htmlFor="supervisor_id">
              Supervisor {roleRequiresSupervisor(selectedRole) ? '(Required)' : '(Optional)'}
              {availableSupervisors.length === 0 && selectedRole && (
                <span style={{ fontSize: '0.8em', color: 'var(--error-color, #d32f2f)', fontWeight: 'normal' }}>
                  {' '}- No supervisors available! Cannot create this role.
                </span>
              )}
            </label>
            <select
              id="supervisor_id"
              {...register('supervisor_id')}
              className={errors.supervisor_id ? 'error' : ''}
              disabled={loading || availableSupervisors.length === 0}
            >
              <option value="">
                {availableSupervisors.length === 0 
                  ? 'No supervisors available' 
                  : `Select a supervisor ${roleRequiresSupervisor(selectedRole) ? '(required)' : '(optional)'}`
                }
              </option>
              {availableSupervisors.map(supervisor => (
                <option key={supervisor.id} value={supervisor.id}>
                  {supervisor.name} - {supervisor.role.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                </option>
              ))}
            </select>
            {errors.supervisor_id && <span className="field-error">{errors.supervisor_id.message}</span>}
            {availableSupervisors.length > 0 && (
              <small style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>
                The invited user will be automatically assigned to the selected supervisor upon signup.
              </small>
            )}
            {availableSupervisors.length === 0 && roleRequiresSupervisor(selectedRole) && (
              <small style={{ color: 'var(--error-color, #d32f2f)', fontSize: '0.875rem' }}>
                ⚠️ You must create a supervisor before inviting users for this role.
              </small>
            )}
          </div>
        )}

        <div className="form-buttons">
          {onCancel && (
            <button 
              type="button"
              onClick={onCancel}
              disabled={loading}
              className="btn btn-outline-secondary"
            >
              Cancel
            </button>
          )}
          <button 
            type="submit" 
            disabled={loading}
            className="btn btn-primary"
          >
            {loading ? 'Creating...' : 'Create Invite'}
          </button>
        </div>

        {success && (
          <div style={{ 
            marginTop: '1rem', 
            padding: '1rem', 
            background: 'var(--bg-tertiary)', 
            borderRadius: '8px',
            fontSize: '0.875rem',
            color: 'var(--text-secondary)'
          }}>
            <strong>Important:</strong> Share this invite token with the user. 
            They will need it to complete their registration.
          </div>
        )}
      </form>
      
      {/* Loading Screen Overlay */}
      <LoadingScreen isVisible={loading} message="Creating invitation..." />
    </div>
  );
};